import { yupResolver } from "@hookform/resolvers/yup"
import { useState } from "react"
import { Helmet } from "react-helmet-async"
import { useForm, Controller } from "react-hook-form"
import * as Yup from "yup"

import api from "apis"
import { Paragraph } from "areas/order/components"
import Button from "components/ui/button"
import FloatInput from "components/ui/float-input"
import Select from "components/ui/select"
import Textarea from "components/ui/textarea"
import { IS_JEWLR } from "helpers/application"

const getCurrentTimeInTimeZone = (timezone) => {
  let options = {
    hour: "numeric",
    hour12: false,
    minute: "numeric",

    timeZone: timezone,
  }

  return new Date().toLocaleTimeString([], options).replace(":", "")
}
const current_time_string = getCurrentTimeInTimeZone("America/Toronto")

const formValidationSchema = Yup.object().shape({
  case_type: Yup.string().required("Please make a selection"),
  customer_email: Yup.string()
    .email("Invalid email address")
    .required("Your email is required"),
  customer_name: Yup.string()
    .matches(/\w+(\s+\w+)+/, { message: "Please enter your full name" })
    .required("Your name is required"),
  customer_phone: Yup.string()
    .matches(/^[\d\-+(). ]{10,20}$/, {
      message: "Please enter a correct phone number",
    })
    .required("Your phone number is required"),
  message: Yup.string().required("Please provide your message"),
  order_number: Yup.string().matches(/^(J|j|L|l|B|b)[\d]{10}(-\d{1,7})?$/, {
    excludeEmptyString: true,
    message: "Please enter a proper order number",
  }),
})

export const Contact = () => {
  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    setError,
  } = useForm({
    defaultValues: {
      case_type: "",
      customer_email: "",
      customer_name: "",
      customer_phone: "",
      merchant: IS_JEWLR() ? "Jewlr" : "Portal",
      message: "",
      order_number: "",
    },
    mode: "onBlur",
    resolver: yupResolver(formValidationSchema),
  })
  const [status, setStatus] = useState(false)

  const onSubmit = async (values) => {
    try {
      let url = "/secure/api/v10/order/contact"
      let response = await api.post(url, values)
      if (response && response.data.done) {
        setStatus(response.data.done)
      }
    } catch (err) {
      console.log(err)
      setError("root.error", {
        message: err?.errors?.[0] || err?.message || err,
      })
    }
  }

  return (
    <>
      <Helmet>
        <title>Contact Us</title>
      </Helmet>
      <div className="tablet:max-w-725 tablet:px-0 mx-auto mb-40 px-20 pt-20">
        {status ? (
          <Paragraph>
            Thank you for contacting us. Please allow up to two business days
            for our representatives to get back to you.
          </Paragraph>
        ) : (
          <>
            <form onSubmit={handleSubmit(onSubmit)}>
              {errors?.root?.error && (
                <p className="text-error mb-8">{errors.root.error.message}</p>
              )}
              <Controller
                control={control}
                name="customer_name"
                render={({ field, fieldState: { error } }) => (
                  <FloatInput
                    autoComplete="name"
                    containerClassName="mb-16"
                    error={error}
                    id="customer_name"
                    label="Your Name:"
                    {...field}
                  />
                )}
              />
              <Controller
                control={control}
                name="customer_email"
                render={({ field, fieldState: { error } }) => (
                  <FloatInput
                    autoComplete="email"
                    containerClassName="mb-16"
                    error={error}
                    id="customer_email"
                    label="Your Email:"
                    {...field}
                  />
                )}
              />
              <Controller
                control={control}
                name="customer_phone"
                render={({ field, fieldState: { error } }) => (
                  <FloatInput
                    autoComplete="tel"
                    containerClassName="mb-16"
                    error={error}
                    id="customer_phone"
                    label="Your Phone:"
                    {...field}
                  />
                )}
              />
              <Controller
                control={control}
                name="order_number"
                render={({ field, fieldState: { error } }) => (
                  <FloatInput
                    autoComplete="off"
                    containerClassName="mb-16"
                    error={error}
                    id="order_number"
                    label="Order # (if you have one):"
                    {...field}
                  />
                )}
              />
              <Controller
                control={control}
                name="case_type"
                render={({ field, fieldState: { error } }) => (
                  <div className="mb-16">
                    <Select error={error} {...field}>
                      <option disabled value="">
                        How can we help?
                      </option>
                      <option value="Pre-order Inquiry">
                        Need information before ordering
                      </option>
                      <option value="Payment Issue">Payment Issues</option>
                      {IS_JEWLR() && (
                        <option value="Shipping Upgrade">
                          Upgrade my shipping method
                        </option>
                      )}
                      <option value="Order Status">
                        Where&apos;s my order?
                      </option>
                      <option value="Change Request">
                        Make a change to my order
                      </option>
                      {IS_JEWLR() && (
                        <option value="Unsubscribe">
                          Remove me from your mailing list
                        </option>
                      )}
                      <option value="Other">Other</option>
                    </Select>
                  </div>
                )}
              />
              <Controller
                control={control}
                name="message"
                render={({ field, fieldState: { error } }) => (
                  <Textarea
                    containerClassName="mb-16"
                    error={error}
                    id="message"
                    placeholder="Your Message:"
                    rows="3"
                    {...field}
                  />
                )}
              />
              <div className="mb-16 w-full">
                <Button
                  className="w-full"
                  loading={isSubmitting}
                  type="submit"
                  variant="secondary"
                >
                  {isSubmitting ? "Sending..." : "Send"}
                </Button>
              </div>
            </form>
            <Paragraph>
              For further assistance, feel free to directly email us at{" "}
              <a href={`mailto:${sl.config.customer_service_email}`}>
                {sl.config.customer_service_email}
              </a>{" "}
              {IS_JEWLR() && (
                <>
                  {current_time_string >= "0900" &&
                    current_time_string <= "1730" && (
                      <>
                        or call us at{" "}
                        <a href={`tel:${sl.config.customer_service_toll_free}`}>
                          {sl.config.customer_service_toll_free}
                        </a>
                      </>
                    )}{" "}
                </>
              )}
              .
            </Paragraph>
          </>
        )}
      </div>
    </>
  )
}

export default Contact
